import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { PageShell, StatCard, StatusBadge } from '@/components/ds';
import { NeedsReviewBadge } from '@/components/contracts/NeedsReviewBadge';
import {
  contractRequestAPI, agentAPI, carrierAPI, contractTypeAPI, levelAPI,
} from '@/lib/api';
import { isKRRole, formatDate, getErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ContractManageDialog } from '@/components/agent/dialogs/ContractManageDialog';
import {
  ClipboardList, Eye, CheckCircle, XCircle, Clock, AlertTriangle,
  Loader2, Plus, Trash2, Archive, Search, X, SlidersHorizontal,
  ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight,
  Send, ShieldCheck, Inbox, HelpCircle, MoreHorizontal,
} from 'lucide-react';
import {
  PIPELINE_STAGE_LABELS,
  statusToStage,
  statusToBadgeVariant,
  countRequestsByStage,
  statusesForStage,
} from '@/lib/contracting/statusMapping';

// Terminal states drive the archive-vs-delete decision. Kept separate from
// the display taxonomy because it's a workflow guarantee, not a UI grouping.
const TERMINAL_REQUEST_STATES = ["Approved", "Declined", "Terminated", "Closed", "Archived"];

// Local metadata (icon / tone / description) for the pipeline strip. Labels
// come from PIPELINE_STAGE_LABELS so terminology stays aligned with the
// Agent and Agency Command Center views.
const STAGE_META = [
  { id: 'new',      icon: Inbox,          tone: 'brand',   description: 'Just submitted — awaiting first KR review.' },
  { id: 'review',   icon: Send,           tone: 'info',    description: 'In flight with KR contracting or the carrier.' },
  { id: 'missing',  icon: HelpCircle,     tone: 'warning', description: 'Waiting on documents or clarification from the agent/agency.' },
  { id: 'approved', icon: ShieldCheck,    tone: 'success', description: 'Carrier signed off on the contract.' },
  { id: 'other',    icon: MoreHorizontal, tone: 'neutral', description: 'Terminal or unclassified statuses (declined, terminated, archived).' },
];

export default function RequestsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isKR = isKRRole(user);

  // Server state
  const [contracts, setContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('carrier_name');
  const [sortDir, setSortDir] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Filters
  const [filters, setFilters] = useState({ status: 'all', carrier_id: 'all', agency_id: 'all', agent_npn: 'all' });
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState(null);

  // Pipeline stage filter (business-friendly roll-up of statuses).
  // Phase 27.13 — accept an initial stage from the URL (?stage=new)
  // so Operations Center KPI cards can deep-link into a pre-filtered view.
  const [searchParams] = useSearchParams();
  const initialStage = (() => {
    const raw = searchParams.get('stage');
    return ['new', 'review', 'missing', 'approved', 'other', 'all'].includes(raw) ? raw : 'all';
  })();
  const [stage, setStage] = useState(initialStage);
  const [stageCounts, setStageCounts] = useState({ new: 0, review: 0, missing: 0, approved: 0, other: 0, all: 0 });

  // Dialogs
  const [showContractDialog, setShowContractDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  // Bulk delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Bulk approve
  const [showBulkApprove, setShowBulkApprove] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);

  // Catalog data for the unified ContractManageDialog. We load it once at
  // page mount so the dialog opens instantly when the operator clicks
  // Manage. These are global reference tables so a stale copy is a
  // non-issue in practice.
  const [carriers, setCarriers] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [levels, setLevels] = useState([]);

  // Debounce search
  const searchTimer = useRef(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(searchTerm); setPage(1); }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm]);

  // Load filter options (using legacy endpoint for full data + agents list)
  useEffect(() => {
    Promise.all([
      contractRequestAPI.list(),
      agentAPI.simpleList(),
      carrierAPI.list(),
      contractTypeAPI.list(),
      levelAPI.list(),
    ]).then(([res, agentsRes, carriersRes, typesRes, levelsRes]) => {
      const data = res.data;
      const statuses = [...new Set(data.map(r => r.current_status).filter(Boolean))].sort();
      const carrierIdMap = {};
      data.forEach(r => { if (r.carrier_id && r.carrier_name) carrierIdMap[r.carrier_id] = r.carrier_name; });
      const agencyIdMap = {};
      data.forEach(r => { if (r.requesting_agency_id && r.agency_name) agencyIdMap[r.requesting_agency_id] = r.agency_name; });
      setFilterOptions({
        statuses: statuses.map(s => ({ value: s, label: s.replace(/_/g, ' ') })),
        carriers: Object.entries(carrierIdMap).map(([id, name]) => ({ value: id, label: name })),
        agencies: Object.entries(agencyIdMap).map(([id, name]) => ({ value: id, label: name })),
        agents: (agentsRes.data || []).map(a => ({ value: a.npn, label: a.name })),
      });
      // Feed the unified ContractManageDialog with the full catalog so
      // it can render carrier/level lists exactly like the Agent Profile
      // page does — same component, same options everywhere.
      setCarriers(carriersRes.data || []);
      setContractTypes(typesRes.data || []);
      setLevels(levelsRes.data || []);
      // Derive pipeline stage counts using the shared taxonomy so the strip
      // reconciles with agent/agency pipelines. `countRequestsByStage`
      // handles both canonical and legacy status spellings.
      const bucketed = countRequestsByStage(data);
      setStageCounts({ ...bucketed, all: data.length });
    }).catch((err) => {
      // Phase 27.7 — surface the error in console so the empty filter
      // panel doesn't look silently broken. A dedicated retry lives on
      // the panel itself via the "Loading filter options…" fallback.
      console.error('Failed to load contract filter options:', err);
    });
  }, []);

  // Load data
  useEffect(() => { loadData(); }, [page, pageSize, sortBy, sortDir, debouncedSearch, filters, stage]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Translate the selected business stage into a raw-status filter list
      // via the shared `statusesForStage` helper — one source of truth for
      // the mapping, including the "Other" negative bucket.
      const statusInList = stage !== 'all' ? statusesForStage(stage) : undefined;
      const params = {
        page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir,
        search: debouncedSearch || undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
        status_in: statusInList ? statusInList.join(',') : undefined,
        carrier_id: filters.carrier_id !== 'all' ? filters.carrier_id : undefined,
        agency_id: filters.agency_id !== 'all' ? filters.agency_id : undefined,
        agent_npn: filters.agent_npn !== 'all' ? filters.agent_npn : undefined,
      };
      const res = await contractRequestAPI.listPaginated(params);
      setContracts(res.data.items);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const handleStageSelect = (id) => {
    setStage((prev) => (prev === id ? 'all' : id));
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
    setPage(1);
  };

  const handleFilterChange = (key, value) => { setFilters(prev => ({ ...prev, [key]: value })); setPage(1); };
  const clearFilters = () => { setFilters({ status: 'all', carrier_id: 'all', agency_id: 'all', agent_npn: 'all' }); setPage(1); };
  const activeFilterCount = Object.values(filters).filter(v => v !== 'all').length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Status change is now handled by the unified ContractManageDialog so
  // there is no per-status form here anymore. The dialog itself calls
  // contractRequestAPI.updateStatus + upsertReleaseFollowUp +
  // appointmentAPI.createDirect in the same submit for full audit trail.

  // Delete — the user asked for a real, permanent delete (not
  // archive), so we always call hardDelete which forces the backend
  // to purge even terminal requests along with their workflow rows.
  const handleDelete = async () => {
    if (!requestToDelete) return;
    try {
      await contractRequestAPI.hardDelete(requestToDelete.request_id);
      toast.success('Request permanently deleted');
      setShowDeleteDialog(false); setRequestToDelete(null); loadData();
    } catch (error) { toast.error(getErrorMessage(error, 'Failed')); }
  };

  // Bulk delete — permanently purge every checked row + its workflow
  // history. Selection auto-clears on success so a subsequent bulk
  // action can't accidentally re-target stale rows.
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const { data } = await contractRequestAPI.bulkDelete(ids, true);
      const parts = [];
      if (data.deleted_count) parts.push(`${data.deleted_count} deleted`);
      if (data.archived_count) parts.push(`${data.archived_count} archived`);
      if (data.not_found_count) parts.push(`${data.not_found_count} not found`);
      toast.success(parts.join(' · ') || 'Nothing to delete');
      setSelectedIds(new Set());
      setShowBulkDelete(false);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Bulk delete failed'));
    } finally {
      setBulkDeleting(false);
    }
  };

  // Bulk approve — approve every eligible checked row; the backend skips
  // incomplete (Missing Info) and already-approved requests and tells us
  // which ones it skipped so we can surface that to the operator.
  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    setBulkApproving(true);
    try {
      const ids = Array.from(selectedIds);
      const { data } = await contractRequestAPI.bulkApprove(ids);
      if (data.approved_count > 0) {
        toast.success(`${data.approved_count} contract${data.approved_count === 1 ? '' : 's'} approved`);
      }
      if (data.skipped_count > 0) {
        const miss = data.skipped.filter((s) => s.reason === 'missing_info').length;
        const already = data.skipped.filter((s) => s.reason === 'already_approved').length;
        const other = data.skipped_count - miss - already;
        const parts = [];
        if (miss) parts.push(`${miss} incomplete (Missing Info)`);
        if (already) parts.push(`${already} already approved`);
        if (other) parts.push(`${other} skipped`);
        toast.warning(`Skipped: ${parts.join(' · ')}`);
      }
      if (data.approved_count === 0 && data.skipped_count === 0) {
        toast.info('Nothing to approve');
      }
      setSelectedIds(new Set());
      setShowBulkApprove(false);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Bulk approve failed'));
    } finally {
      setBulkApproving(false);
    }
  };

  const toggleRowSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllSelected = () => {
    setSelectedIds((prev) => {
      // If every visible row is already selected, clear. Otherwise,
      // select every currently-visible row.
      const allSelected = contracts.length > 0 && contracts.every((c) => prev.has(c.request_id));
      if (allSelected) return new Set();
      return new Set(contracts.map((c) => c.request_id));
    });
  };

  // Any time filters/search/stage change and new rows appear, drop
  // ids from the selection set that no longer exist on-screen — the
  // "delete N selected" banner would otherwise lie about the target.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(contracts.map((c) => c.request_id));
      const filtered = new Set([...prev].filter((id) => visible.has(id)));
      return filtered.size === prev.size ? prev : filtered;
    });
  }, [contracts]);

  // Row status icon derived from the shared taxonomy — one place decides
  // what "success", "warning", "info" look like across the whole app.
  const getStatusIcon = (status) => {
    const st = statusToStage(status);
    if (st === 'approved') return <CheckCircle className="text-emerald-500" size={14} />;
    if (status === 'Declined' || status === 'Terminated') return <XCircle className="text-red-500" size={14} />;
    // Phase 27.38 — `Closed` is a neutral terminal (abandoned invite),
    // NOT a rejection. Show a slate X so the UI doesn't imply the
    // carrier denied the request.
    if (status === 'Closed') return <XCircle className="text-slate-400" size={14} />;
    if (st === 'missing') return <AlertTriangle className="text-amber-500" size={14} />;
    return <Clock className="text-slate-400" size={14} />;
  };

  const SortHeader = ({ field, label }) => {
    const active = sortBy === field;
    return (
      <button className="flex items-center gap-1 hover:text-slate-800 transition-colors group whitespace-nowrap"
        onClick={() => handleSort(field)} data-testid={`sort-${field}`}>
        <span>{label}</span>
        {active ? (sortDir === 'asc' ? <ArrowUp size={13} className="text-blue-600" /> : <ArrowDown size={13} className="text-blue-600" />) :
          <ArrowUpDown size={13} className="text-slate-300 group-hover:text-slate-400" />}
      </button>
    );
  };

  return (
    <PageShell
      title="Contracts"
      description="Your unified contract workspace. Every row here is the same canonical contract that appears on the Agent Profile — filtered by workflow status: New, Under Review, Missing Information, Approved, Other."
      breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Contracting' }, { label: 'Contracts' }]}
      actions={!isKR && <Link to="/request-contract"><Button data-testid="new-request-btn"><Plus size={16} className="mr-2" />New Contract</Button></Link>}
      testId="requests-page"
    >
      {/* Pipeline strip — 5 business stages from the shared taxonomy. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 mb-5" data-testid="pipeline-strip">
        {STAGE_META.map((s) => (
          <StatCard
            key={s.id}
            label={PIPELINE_STAGE_LABELS[s.id]}
            value={stageCounts[s.id] ?? 0}
            icon={s.icon}
            tone={s.tone}
            hint={s.description}
            onClick={() => handleStageSelect(s.id)}
            className={stage === s.id ? 'ring-2 ring-[color:var(--kr-gold-500)] border-[color:var(--kr-gold-500)]' : ''}
            testId={`pipeline-stage-${s.id}`}
          />
        ))}
      </div>

      {stage !== 'all' && (
        <div
          className="flex items-center justify-between mb-3 px-3 py-2 rounded-lg"
          style={{ background: 'var(--kr-navy-50)', border: '1px solid var(--kr-navy-100)' }}
          data-testid="stage-filter-active"
        >
          <span className="text-sm" style={{ color: 'var(--kr-navy-800)' }}>
            Filtered by <strong>{PIPELINE_STAGE_LABELS[stage]}</strong> — {total} matching request{total !== 1 ? 's' : ''}
          </span>
          <button
            type="button"
            className="text-xs font-medium"
            style={{ color: 'var(--kr-navy-700)' }}
            onClick={() => setStage('all')}
            data-testid="stage-filter-clear"
          >
            Clear stage filter
          </button>
        </div>
      )}

      <div className="page-content">
        <Card>
          <CardContent className="pt-5">
            {/* Search + Filter Bar */}
            <div className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="contracts-toolbar">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search by carrier, agent, agency, NPN..."
                  className="pl-9 h-9 text-sm" data-testid="contracts-search-input" />
                {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={14} /></button>}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-9 gap-1.5 shrink-0"
                  onClick={() => setShowFilters(!showFilters)} data-testid="toggle-contract-filters-btn">
                  <SlidersHorizontal size={14} />
                  <span className="hidden sm:inline">Filters</span>
                  {activeFilterCount > 0 && <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center">{activeFilterCount}</span>}
                </Button>
                <div className="text-xs text-slate-500 px-2 whitespace-nowrap">{total} contract{total !== 1 ? 's' : ''}</div>
              </div>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="flex flex-wrap gap-2 p-3 mb-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="contract-filter-panel">
                {filterOptions ? (
                  <>
                    <FilterSelect label="Status" value={filters.status} options={filterOptions.statuses} onChange={v => handleFilterChange('status', v)} testId="filter-contract-status" />
                    <FilterSelect label="Carrier" value={filters.carrier_id} options={filterOptions.carriers} onChange={v => handleFilterChange('carrier_id', v)} testId="filter-contract-carrier" />
                    <FilterSelect label="Agent" value={filters.agent_npn} options={filterOptions.agents} onChange={v => handleFilterChange('agent_npn', v)} testId="filter-contract-agent" />
                    <FilterSelect label="Agency" value={filters.agency_id} options={filterOptions.agencies} onChange={v => handleFilterChange('agency_id', v)} testId="filter-contract-agency" />
                    {activeFilterCount > 0 && <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={clearFilters} data-testid="clear-contract-filters">Clear all</Button>}
                  </>
                ) : (
                  <span className="text-xs text-slate-500 px-2 py-1" data-testid="contract-filter-loading">
                    Loading filter options…
                  </span>
                )}
              </div>
            )}

            {/* Table */}
            {loading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
            ) : contracts.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <ClipboardList size={40} className="mx-auto mb-3 text-slate-300" />
                <p className="font-medium text-slate-700 mb-1">{debouncedSearch || activeFilterCount > 0 ? 'No results found' : 'No contract requests found'}</p>
                <p className="text-sm">{debouncedSearch || activeFilterCount > 0 ? 'Try adjusting your search or filters.' : 'Contract requests will appear here.'}</p>
              </div>
            ) : (
              <div className="rounded-md border border-slate-200 overflow-x-auto bg-white">
                {/* Bulk selection banner — surfaces the count + action
                    row-actions once at least one row is ticked. */}
                {isKR && selectedIds.size > 0 && (
                  <div className="flex items-center justify-between gap-3 px-4 py-2 bg-blue-50 border-b border-blue-200 text-sm" data-testid="bulk-selection-banner">
                    <div className="text-blue-900 font-medium">
                      {selectedIds.size} {selectedIds.size === 1 ? 'request' : 'requests'} selected
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedIds(new Set())}
                        data-testid="bulk-clear-selection"
                      >
                        Clear
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => setShowBulkApprove(true)}
                        data-testid="bulk-approve-btn"
                      >
                        <CheckCircle size={14} className="mr-1.5" />
                        Approve selected
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="bg-red-600 hover:bg-red-700"
                        onClick={() => setShowBulkDelete(true)}
                        data-testid="bulk-delete-btn"
                      >
                        <Trash2 size={14} className="mr-1.5" />
                        Delete selected
                      </Button>
                    </div>
                  </div>
                )}
                <table className="w-full text-sm" data-testid="contracts-table">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      {isKR && (
                        <th className="px-3 py-3 text-left w-9">
                          <Checkbox
                            checked={contracts.length > 0 && contracts.every((c) => selectedIds.has(c.request_id))}
                            onCheckedChange={toggleAllSelected}
                            data-testid="bulk-select-all"
                            aria-label="Select all rows"
                          />
                        </th>
                      )}
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><SortHeader field="carrier_name" label="Carrier" /></th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><SortHeader field="agent_name" label="Agent" /></th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden lg:table-cell"><SortHeader field="agency_name" label="Agency" /></th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell"><SortHeader field="level_name" label="Contract Level" /></th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden xl:table-cell">Writing #</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><SortHeader field="current_status" label="Status" /></th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden xl:table-cell"><SortHeader field="updated_at" label="Last Updated" /></th>
                      <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 w-28">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {contracts.map((c) => (
                      <tr key={c.request_id} className={`hover:bg-slate-50/80 transition-colors cursor-pointer group ${selectedIds.has(c.request_id) ? 'bg-blue-50/40' : ''}`} data-testid={`contract-row-${c.request_id}`}
                        onClick={() => navigate(`/requests/${c.request_id}`)}>
                        {isKR && (
                          <td className="px-3 py-3 w-9" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedIds.has(c.request_id)}
                              onCheckedChange={() => toggleRowSelected(c.request_id)}
                              data-testid={`row-select-${c.request_id}`}
                              aria-label={`Select ${c.carrier_name} request`}
                            />
                          </td>
                        )}
                        <td className="px-3 py-3">
                          <span className="font-medium text-slate-900 group-hover:text-blue-700 transition-colors">{c.carrier_name}</span>
                          <span className="block text-xs text-slate-400 md:hidden">{c.level_name}</span>
                        </td>
                        <td className="px-3 py-3">
                          <span className="text-sky-700 hover:text-sky-900 cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); navigate(`/agents/${c.npn}`); }}>{c.agent_name}</span>
                          <span className="block text-xs text-slate-400 font-mono">{c.npn}</span>
                        </td>
                        <td className="px-3 py-3 text-slate-600 hidden lg:table-cell">{c.agency_name}</td>
                        <td className="px-3 py-3 text-slate-600 hidden md:table-cell">{c.level_name}</td>
                        <td className="px-3 py-3 hidden xl:table-cell">
                          {c.writing_number ? <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">{c.writing_number}</span> : <span className="text-slate-300">-</span>}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {getStatusIcon(c.current_status)}
                            <StatusBadge
                              variant={statusToBadgeVariant(c.current_status)}
                              testId={`status-badge-${c.current_status}`}
                            >
                              {c.current_status?.replace(/_/g, ' ')}
                            </StatusBadge>
                            <NeedsReviewBadge contract={c} className="text-[10px] py-0 px-1.5" />
                          </div>
                        </td>
                        <td className="px-3 py-3 text-xs text-slate-400 hidden xl:table-cell">{formatDate(c.updated_at || c.created_at)}</td>
                        <td className="px-3 py-3 text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => navigate(`/requests/${c.request_id}`)} data-testid={`view-request-${c.request_id}`}><Eye size={14} /></Button>
                            {isKR && (
                              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setSelectedRequest(c); setShowContractDialog(true); }} data-testid={`change-status-${c.request_id}`}>Manage</Button>
                            )}
                            {isKR && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => { setRequestToDelete(c); setShowDeleteDialog(true); }} data-testid={`delete-request-${c.request_id}`}><Trash2 size={14} /></Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {total > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 mt-4 border-t border-slate-100" data-testid="contracts-pagination">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>Rows per page</span>
                  <Select value={String(pageSize)} onValueChange={v => { setPageSize(Number(v)); setPage(1); }}>
                    <SelectTrigger className="h-7 w-[65px] text-xs" data-testid="contract-page-size"><SelectValue /></SelectTrigger>
                    <SelectContent>{[25, 50, 100].map(s => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">{total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="contract-prev-page"><ChevronLeft size={14} /></Button>
                    {totalPages <= 5 ? (
                      Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                        <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={() => setPage(p)}>{p}</Button>
                      ))
                    ) : (
                      <>
                        <Button variant={page === 1 ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={() => setPage(1)}>1</Button>
                        {page > 3 && <span className="text-xs text-slate-400 px-1">...</span>}
                        {Array.from({ length: 3 }, (_, i) => Math.min(Math.max(page - 1 + i, 2), totalPages - 1))
                          .filter((p, i, a) => a.indexOf(p) === i && p > 1 && p < totalPages)
                          .map(p => <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={() => setPage(p)}>{p}</Button>)}
                        {page < totalPages - 2 && <span className="text-xs text-slate-400 px-1">...</span>}
                        <Button variant={page === totalPages ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={() => setPage(totalPages)}>{totalPages}</Button>
                      </>
                    )}
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="contract-next-page"><ChevronRight size={14} /></Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Unified contract management dialog — same component the Agent
          Profile page uses. Handles status changes, release follow-up,
          appointment fields, and internal notes in one submit. */}
      <ContractManageDialog
        open={showContractDialog}
        onOpenChange={(v) => { setShowContractDialog(v); if (!v) setSelectedRequest(null); }}
        agent={selectedRequest ? {
          npn: selectedRequest.npn,
          agency_id: selectedRequest.requesting_agency_id,
        } : null}
        editing={selectedRequest}
        carriers={carriers}
        contractTypes={contractTypes}
        levels={levels}
        requests={[]}
        appointments={[]}
        onSaved={loadData}
      />

      {/* Bulk Approve Confirmation */}
      <AlertDialog open={showBulkApprove} onOpenChange={setShowBulkApprove}>
        <AlertDialogContent data-testid="bulk-approve-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve {selectedIds.size} {selectedIds.size === 1 ? 'contract' : 'contracts'}</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block mb-3">
                Every eligible ticked request will be marked <strong>Approved</strong> and
                the agent/agency will be notified — same as approving them one by one.
              </span>
              <span className="block text-slate-600">
                Requests that are <strong>incomplete (Missing Info)</strong> or
                <strong> already approved</strong> are skipped automatically, and
                you'll get a summary of what was skipped.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkApproving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkApprove}
              disabled={bulkApproving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-bulk-approve-btn"
            >
              {bulkApproving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CheckCircle size={14} className="mr-1" />}
              Approve {selectedIds.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={showBulkDelete} onOpenChange={setShowBulkDelete}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} {selectedIds.size === 1 ? 'request' : 'requests'}</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block mb-3">
                Every ticked request and its complete workflow history will be
                <strong> permanently deleted</strong>. This applies to active AND
                terminal (Approved / Declined / Terminated) requests.
              </span>
              <span className="text-red-600">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-bulk-delete-btn"
            >
              {bulkDeleting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Trash2 size={14} className="mr-1" />}
              Delete {selectedIds.size} permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog — always permanent, per user request */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="requests-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Request</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block mb-3">Agent: <strong>{requestToDelete?.agent_name}</strong><br />Carrier: <strong>{requestToDelete?.carrier_name}</strong><br />Status: <strong>{requestToDelete?.current_status?.replace(/_/g, ' ')}</strong></span>
              <span className="text-red-600">
                This request and every workflow history row will be <strong>permanently deleted</strong>. This action cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-btn">
              <Trash2 size={14} className="mr-1" />Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}

function FilterSelect({ label, value, options, onChange, testId }) {
  return (
    <div className="min-w-[140px]">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs bg-white" data-testid={testId}><SelectValue placeholder={label} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All {label}</SelectItem>
          {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
